import React, { useState, useRef } from "react";
import { Type, Droplet, TextCursorInput, ChevronDown, PaintBucket } from "lucide-react";

const StatusPage: React.FC = () => {
  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [fontSize, setFontSize] = useState(36);
  const [fontFamily, setFontFamily] = useState("sans-serif");
  const [showPanel, setShowPanel] = useState<null | "font" | "textColor" | "bgColor" | "size">(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const togglePanel = (panel: typeof showPanel) => {
    setShowPanel(showPanel === panel ? null : panel);
  };

  return (
    <div className="relative w-full h-screen" style={{ backgroundColor: bgColor }}>
      {/* Header */}
      <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-center text-white bg-black z-10">
        <span>Status</span>
        <button>➡️</button>
      </div>

      {/* Editable Text */}
      <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
        <div
          ref={contentRef}
          contentEditable
          suppressContentEditableWarning
          className="outline-none font-bold break-words max-w-full"
          style={{
            color: textColor,
            fontSize: `${fontSize}px`,
            fontFamily: fontFamily,
          }}
        >
          Hello world
        </div>
      </div>

      {/* Floating Buttons */}
      <div className="absolute top-1/2 right-4 -translate-y-1/2 flex flex-col space-y-4 z-10">
        <button onClick={() => togglePanel("font")} className="bg-white p-2 rounded-full shadow">
          <Type className="w-5 h-5" />
        </button>
        <button onClick={() => togglePanel("textColor")} className="bg-white p-2 rounded-full shadow">
          <Droplet className="w-5 h-5" />
        </button>
        <button onClick={() => togglePanel("bgColor")} className="bg-white p-2 rounded-full shadow">
          <PaintBucket className="w-5 h-5" />
        </button>
        <button onClick={() => togglePanel("size")} className="bg-white p-2 rounded-full shadow">
          <TextCursorInput className="w-5 h-5" />
        </button>
      </div>

      {/* Bottom Panel */}
      {showPanel && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/90 text-white p-4 rounded-t-xl space-y-4 z-20">
          <div className="flex justify-between items-center">
            <span className="capitalize text-base font-semibold">{showPanel} settings</span>
            <button onClick={() => setShowPanel(null)}>
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          {showPanel === "textColor" && (
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <span>Text</span>
                <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
              </label>
            </div>
          )}

          {showPanel === "bgColor" && (
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <span>Background</span>
                <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
              </label>
            </div>
          )}

          {showPanel === "size" && (
            <div className="flex items-center space-x-4">
              <input type="range" min={12} max={72} value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} className="w-full" />
              <span>{fontSize}px</span>
            </div>
          )}

          {showPanel === "font" && (
            <div className="flex gap-2 flex-wrap">
              {["sans-serif", "serif", "cursive", "monospace"].map((font) => (
                <button key={font} className="px-3 py-1 bg-white text-black rounded font-semibold text-sm" onClick={() => setFontFamily(font)}>
                  {font}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusPage;
